"use client";
import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table";
import { Loader2, ArrowLeft, Undo2 } from "lucide-react";
import { useToast } from "@/components/ui/use-toast";
import { Link } from "react-router-dom";
import { format } from "date-fns";

const API_URL = import.meta.env.VITE_API_URL;

export default function AddReturnPage() {
    const navigate = useNavigate();
    const searchParams = useSearchParams();
    const { toast } = useToast();

    const [purchases, setPurchases] = useState([]);
    const [selectedPurchase, setSelectedPurchase] = useState(searchParams.get("purchase") || null);
    const [purchaseDetails, setPurchaseDetails] = useState(null);
    const [loading, setLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);

    // Return form state
    const [returnItems, setReturnItems] = useState([]);
    const [reason, setReason] = useState("");
    const [notes, setNotes] = useState("");

    // Fetch all purchases
    useEffect(() => {
        const fetchPurchases = async () => {
            try {
                const res = await axios.get(`${API_URL}purchases?limit=100`, {
                    withCredentials: true,
                });
                setPurchases(res.data?.data || []);
            } catch (err) {
                console.error("Failed to fetch purchases:", err);
            }
        };
        fetchPurchases();
    }, []);

    // Fetch purchase details when selected
    useEffect(() => {
        if (!selectedPurchase) {
            setPurchaseDetails(null);
            setReturnItems([]);
            return;
        }

        const fetchDetails = async () => {
            setLoading(true);
            try {
                const res = await axios.get(`${API_URL}purchases/${selectedPurchase}`, {
                    withCredentials: true,
                });
                const purchase = res.data?.data;
                setPurchaseDetails(purchase);

                // Initialize return items from purchase stocks
                const items = (purchase?.Stocks || []).map(stock => ({
                    product_id: stock.product_id,
                    product_name: stock.Product?.title || "Unknown Product",
                    available_qty: stock.quantity,
                    return_qty: 0,
                    unit_cost: parseFloat(stock.data?.cost) || 0,
                    reason: "defective",
                    selected: false,
                }));
                setReturnItems(items);
            } catch (err) {
                console.error("Failed to fetch purchase details:", err);
                toast({ variant: "destructive", title: "Error", description: "Failed to load purchase details" });
            } finally {
                setLoading(false);
            }
        };
        fetchDetails();
    }, [selectedPurchase, toast]);

    const toggleItem = (index) => {
        setReturnItems(items => items.map((item, i) =>
            i === index ? { ...item, selected: !item.selected } : item
        ));
    };

    const updateReturnQty = (index, qty) => {
        setReturnItems(items => items.map((item, i) =>
            i === index ? { ...item, return_qty: Math.min(qty, item.available_qty) } : item
        ));
    };

    const updateItemReason = (index, reason) => {
        setReturnItems(items => items.map((item, i) =>
            i === index ? { ...item, reason } : item
        ));
    };

    const selectedItems = returnItems.filter(item => item.selected && item.return_qty > 0);
    const totalReturnValue = selectedItems.reduce((sum, item) =>
        sum + (item.return_qty * item.unit_cost), 0
    );

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!selectedPurchase) {
            toast({ variant: "destructive", title: "Error", description: "Please select a purchase" });
            return;
        }

        if (selectedItems.length === 0) {
            toast({ variant: "destructive", title: "Error", description: "Select at least one item to return" });
            return;
        }

        setSubmitting(true);
        try {
            await axios.post(`${API_URL}purchases/returns`, {
                purchase_id: selectedPurchase,
                items: selectedItems.map(item => ({
                    product_id: item.product_id,
                    quantity: item.return_qty,
                    unit_cost: item.unit_cost,
                    reason: item.reason,
                })),
                reason,
                notes,
            }, { withCredentials: true });

            toast({ title: "Success", description: "Return created successfully" });
            navigate("/dashboard/returns");
        } catch (err) {
            console.error("Failed to create return:", err);
            toast({ variant: "destructive", title: "Error", description: err.response?.data?.message || "Failed to create return" });
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="mx-auto p-4 space-y-6">
            <div className="flex items-center gap-4">
                <Link to="/dashboard/purchases/returns-all/returns">
                    <Button variant="ghost" size="icon">
                        <ArrowLeft className="h-5 w-5" />
                    </Button>
                </Link>
                <h1 className="text-3xl font-bold">Create Purchase Return</h1>
            </div>

            <form onSubmit={handleSubmit}>
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    {/* Left - Main Form */}
                    <div className="lg:col-span-2 space-y-6">
                        {/* Purchase Selection */}
                        <Card>
                            <CardHeader>
                                <CardTitle>Select Purchase</CardTitle>
                            </CardHeader>
                            <CardContent>
                                <Select
                                    value={selectedPurchase?.toString() || ""}
                                    onValueChange={(val) => setSelectedPurchase(val)}
                                >
                                    <SelectTrigger>
                                        <SelectValue placeholder="Select a purchase to return from..." />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {purchases.map((p) => (
                                            <SelectItem key={p.id} value={p.id.toString()}>
                                                {p.invoice_number || `PO-${p.id}`} - {p.Supplier?.name || "Unknown"} - {format(new Date(p.createdAt), "dd MMM yyyy")}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </CardContent>
                        </Card>

                        {/* Items Selection */}
                        <Card>
                            <CardHeader>
                                <CardTitle>Select Items to Return</CardTitle>
                            </CardHeader>
                            <CardContent>
                                {loading ? (
                                    <div className="flex items-center justify-center py-8">
                                        <Loader2 className="h-6 w-6 animate-spin" />
                                    </div>
                                ) : returnItems.length > 0 ? (
                                    <Table>
                                        <TableHeader>
                                            <TableRow>
                                                <TableHead className="w-10"></TableHead>
                                                <TableHead>Product</TableHead>
                                                <TableHead className="w-24">Available</TableHead>
                                                <TableHead className="w-24">Return Qty</TableHead>
                                                <TableHead className="w-32">Reason</TableHead>
                                            </TableRow>
                                        </TableHeader>
                                        <TableBody>
                                            {returnItems.map((item, index) => (
                                                <TableRow key={index}>
                                                    <TableCell>
                                                        <Checkbox
                                                            checked={item.selected}
                                                            onCheckedChange={() => toggleItem(index)}
                                                        />
                                                    </TableCell>
                                                    <TableCell>{item.product_name}</TableCell>
                                                    <TableCell>{item.available_qty}</TableCell>
                                                    <TableCell>
                                                        <Input
                                                            type="number"
                                                            min="0"
                                                            max={item.available_qty}
                                                            value={item.return_qty}
                                                            onChange={(e) => updateReturnQty(index, parseInt(e.target.value) || 0)}
                                                            className="w-20"
                                                            disabled={!item.selected}
                                                        />
                                                    </TableCell>
                                                    <TableCell>
                                                        <Select
                                                            value={item.reason}
                                                            onValueChange={(val) => updateItemReason(index, val)}
                                                            disabled={!item.selected}
                                                        >
                                                            <SelectTrigger className="w-28">
                                                                <SelectValue />
                                                            </SelectTrigger>
                                                            <SelectContent>
                                                                <SelectItem value="damaged">Damaged</SelectItem>
                                                                <SelectItem value="defective">Defective</SelectItem>
                                                                <SelectItem value="wrong_item">Wrong Item</SelectItem>
                                                                <SelectItem value="excess">Excess</SelectItem>
                                                                <SelectItem value="expired">Expired</SelectItem>
                                                                <SelectItem value="other">Other</SelectItem>
                                                            </SelectContent>
                                                        </Select>
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                ) : (
                                    <div className="text-center py-8 text-muted-foreground">
                                        {selectedPurchase ? "No items found in this purchase" : "Select a purchase above to see items"}
                                    </div>
                                )}
                            </CardContent>
                        </Card>

                        {/* Notes */}
                        <Card>
                            <CardHeader>
                                <CardTitle>Return Details</CardTitle>
                            </CardHeader>
                            <CardContent className="space-y-4">
                                <div>
                                    <Label>Overall Reason</Label>
                                    <Input
                                        value={reason}
                                        onChange={(e) => setReason(e.target.value)}
                                        placeholder="e.g., Supplier quality issue"
                                    />
                                </div>
                                <div>
                                    <Label>Notes</Label>
                                    <Textarea
                                        value={notes}
                                        onChange={(e) => setNotes(e.target.value)}
                                        placeholder="Additional details about this return..."
                                        rows={3}
                                    />
                                </div>
                            </CardContent>
                        </Card>
                    </div>

                    {/* Right - Summary */}
                    <div className="space-y-6">
                        <Card>
                            <CardHeader>
                                <CardTitle>Return Summary</CardTitle>
                            </CardHeader>
                            <CardContent className="space-y-3">
                                <div className="flex justify-between">
                                    <span>Items Selected</span>
                                    <span className="font-medium">{selectedItems.length}</span>
                                </div>
                                <div className="flex justify-between">
                                    <span>Total Quantity</span>
                                    <span className="font-medium">
                                        {selectedItems.reduce((sum, i) => sum + i.return_qty, 0)}
                                    </span>
                                </div>
                                <div className="flex justify-between font-bold text-lg border-t pt-3">
                                    <span>Return Value</span>
                                    <span>GHS {totalReturnValue.toFixed(2)}</span>
                                </div>
                            </CardContent>
                        </Card>

                        <Button
                            type="submit"
                            className="w-full"
                            size="lg"
                            disabled={submitting || selectedItems.length === 0}
                        >
                            {submitting ? (
                                <>
                                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                    Creating...
                                </>
                            ) : (
                                <>
                                    <Undo2 className="mr-2 h-4 w-4" />
                                    Create Return
                                </>
                            )}
                        </Button>
                    </div>
                </div>
            </form>
        </div>
    );
}
